import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { ChannelBadge } from "@/components/badges/ChannelBadge";
import { TemperaturePill } from "@/components/badges/TemperaturePill";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { MessageBubble, type MessageRow, type BubblePosition } from "./MessageBubble";
import { ReplyBox } from "./ReplyBox";
import { HandoffLinkModal } from "@/components/handoffs/HandoffLinkModal";
import { renderIdentityFallback } from "@/lib/format";
import { useToast } from "@/lib/toast";
import { Loader2, UserCog, Bot, AlertCircle, MessageCircleHeart } from "lucide-react";
import type {
  Channel,
  ContactStatus,
  LeadTemperature,
  SourceChannel,
} from "@ai-leasing-os/shared";

interface ConvDetail {
  conversation: {
    id: string;
    contact_id: string;
    channel: Channel;
    external_thread_id: string;
    status: string;
    human_takeover: number;
    takeover_reason: string | null;
    source_platform: string | null;
    last_message_at: string | null;
    requires_human_followup: number;
    display_name: string | null;
    primary_phone: string | null;
    score: number;
    awareness_level: string;
    temperature: LeadTemperature;
    contact_status: ContactStatus;
  };
  messages: MessageRow[];
}

export function ConversationPane({
  conversationId,
  onRefreshList,
}: {
  conversationId: string;
  onRefreshList: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ["conversation", conversationId],
    queryFn: () => api.get<ConvDetail>(`/api/conversations/${conversationId}`),
    refetchInterval: 5_000,
  });

  // Phase H7 - Move-to-WhatsApp modal state. The seed/draft pair lets the
  // modal push a wa.me link into the ReplyBox without lifting the
  // textarea's state into this component.
  const [handoffModalOpen, setHandoffModalOpen] = useState(false);
  const [pendingDraft, setPendingDraft] = useState<string | undefined>(undefined);
  const [draftSeed, setDraftSeed] = useState(0);

  function pushReplyDraft(text: string) {
    setPendingDraft(text);
    setDraftSeed((n) => n + 1);
  }

  const takeoverMut = useMutation({
    mutationFn: (reason: string) =>
      api.post(`/api/conversations/${conversationId}/takeover`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conversation", conversationId] });
      onRefreshList();
      toast.success("You're handling this conversation", "The AI is paused here.");
    },
    onError: (e) =>
      toast.error("Could not take over", e instanceof Error ? e.message : undefined),
  });

  const resumeMut = useMutation({
    mutationFn: () =>
      api.post(`/api/conversations/${conversationId}/resume-ai`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conversation", conversationId] });
      onRefreshList();
      toast.success("AI resumed", "The assistant will reply to new messages again.");
    },
    onError: (e) =>
      toast.error("Could not resume AI", e instanceof Error ? e.message : undefined),
  });

  const sendMut = useMutation({
    mutationFn: (text: string) =>
      api.post(`/api/messages`, { conversation_id: conversationId, text }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["conversation", conversationId] });
      onRefreshList();
    },
  });

  if (isLoading || !data) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const conv = data.conversation;
  const inTakeover = conv.human_takeover === 1;

  return (
    <div className="flex h-full flex-col bg-slate-50">
      {/* Header */}
      <header className="shrink-0 border-b border-slate-200 bg-brand-surface px-5 py-3">
        <div className="flex items-center gap-3">
          <ChannelBadge channel={conv.channel} size="md" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900 truncate">
              {conv.display_name ?? renderIdentityFallback(conv)}
              {conv.display_name && conv.primary_phone && (
                <a
                  href={`https://wa.me/${conv.primary_phone}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-2 text-xs font-normal text-slate-500 tabular-nums hover:text-slate-700 hover:underline"
                  title="Open in WhatsApp"
                >
                  +{conv.primary_phone}
                </a>
              )}
              {!conv.display_name &&
                conv.external_thread_id?.endsWith("@lid") && (
                  <span className="ml-2 text-[10px] font-normal text-slate-400">
                    no phone on file
                  </span>
                )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <StatusBadge status={conv.contact_status} />
              <TemperaturePill temperature={conv.temperature} />
              <span className="text-[11px] text-slate-500">
                score {conv.score} · {conv.awareness_level}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(conv.channel === "instagram" || conv.channel === "messenger") && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setHandoffModalOpen(true)}
                title="Generate a tracked WhatsApp link that continues this conversation"
              >
                <MessageCircleHeart className="h-4 w-4" />
                Move to WhatsApp
              </Button>
            )}
            {inTakeover ? (
              <Button
                size="sm"
                variant="primary"
                onClick={() => resumeMut.mutate()}
                disabled={resumeMut.isPending}
              >
                <Bot className="h-4 w-4" />
                Resume AI
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => takeoverMut.mutate("manual takeover from dashboard")}
                disabled={takeoverMut.isPending}
              >
                <UserCog className="h-4 w-4" />
                Take over
              </Button>
            )}
          </div>
        </div>
        {inTakeover && (
          <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 rounded px-2 py-1">
            <AlertCircle className="h-3.5 w-3.5" />
            AI is paused for this conversation{conv.takeover_reason ? ` - ${conv.takeover_reason}` : ""}.
          </div>
        )}
      </header>

      {/* Messages — WhatsApp-style grouping: consecutive bubbles from the
          same actor share one header + one timestamp, with tight spacing
          inside the run. */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {data.messages.length === 0 ? (
          <div className="text-center text-xs text-slate-400 py-12">
            No messages yet.
          </div>
        ) : (
          data.messages.map((m, i) => {
            const prev = data.messages[i - 1];
            const next = data.messages[i + 1];
            const position = bubblePosition(prev, m, next);
            const tightTop = prev && sameRun(prev, m);
            return (
              <div key={m.id} className={tightTop ? "mt-0.5" : "mt-3"}>
                <MessageBubble message={m} position={position} />
              </div>
            );
          })
        )}
      </div>

      {/* Reply box */}
      <div className="shrink-0 border-t border-slate-200 bg-brand-surface p-3">
        <ReplyBox
          onSend={(text) => sendMut.mutate(text)}
          isSending={sendMut.isPending}
          inTakeover={inTakeover}
          pendingDraft={pendingDraft}
          pendingDraftSeed={draftSeed}
        />
      </div>

      {/* Phase H7 - Move-to-WhatsApp modal. Rendered as a sibling so the
          rest of the pane keeps its existing layout. */}
      {(conv.channel === "instagram" || conv.channel === "messenger") && (
        <HandoffLinkModal
          open={handoffModalOpen}
          onClose={() => setHandoffModalOpen(false)}
          initial={{
            mode: "handoff",
            sourceChannel: channelToSourceChannel(conv.channel),
            sourceContactId: conv.contact_id,
            sourceConversationId: conv.id,
            ref: handoffRefFromChannel(conv.channel),
            message: "Hi, I want to continue on WhatsApp.",
            sourceLabel:
              conv.channel === "instagram" ? "Instagram DM" : "Messenger",
          }}
          onInsertIntoReply={pushReplyDraft}
        />
      )}
    </div>
  );
}

/** Map the source IG/Messenger conversation channel to its `SourceChannel`. */
function channelToSourceChannel(channel: Channel): SourceChannel {
  if (channel === "instagram") return "instagram_dm";
  if (channel === "messenger") return "messenger";
  // Defensive - the H7 button only shows for IG/Messenger so this is
  // effectively unreachable, but keep the resolver total.
  return "other";
}

function handoffRefFromChannel(channel: Channel): string {
  if (channel === "instagram") return "instagram_dm_handoff";
  if (channel === "messenger") return "messenger_handoff";
  return "handoff";
}

/**
 * Two messages belong to the same "run" (visual group) when they share
 * actor + direction AND were created within 60 seconds of each other. The
 * time cap keeps a reply 5 minutes later from getting visually merged with
 * the previous burst, which would mislead an operator scanning the chat.
 */
function sameRun(a: MessageRow, b: MessageRow): boolean {
  if (a.actor !== b.actor || a.direction !== b.direction) return false;
  const diffMs = Math.abs(
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
  return diffMs <= 60_000;
}

function bubblePosition(
  prev: MessageRow | undefined,
  curr: MessageRow,
  next: MessageRow | undefined,
): BubblePosition {
  const grouped_with_prev = !!prev && sameRun(prev, curr);
  const grouped_with_next = !!next && sameRun(curr, next);
  if (!grouped_with_prev && !grouped_with_next) return "only";
  if (!grouped_with_prev && grouped_with_next) return "first";
  if (grouped_with_prev && grouped_with_next) return "middle";
  return "last";
}
